import Store from '@/vuex/store';
import Router from '@/router/router';
import Vue from 'vue';
import { number, largerEq, sum, multiply, divide, round, subtract } from 'mathjs';
import { deep_clone } from '@/utilities/';
import { format_date_YYYY_MM_DD, log } from '@/utilities/commons';
import { getSeatPrice, isSeatAvailableForPassenger } from '@liveflight/utilities/seat-map';

const decimals = 2;

export function get_flight_order_body(payload) {
  let flight_order_body = {
    data: {
      type: 'flight-order',
      flightOffers: payload.flight_offers_pricing,
      travelers: [],
      // TODO remarks: {},
      contacts: [
        {
          addresseeName: {
            firstName: payload.contact_details.firstname,
            middleName: payload.contact_details.middlename,
            lastName: payload.contact_details.lastname,
            secondLastName: payload.contact_details.second_lastname,
          },
          // TODO companyName: '',
          purpose: 'STANDARD',
          language: 'IT',
          phones: [
            {
              deviceType: 'MOBILE',
              countryCallingCode: payload.contact_details.country_calling_code,
              number: payload.contact_details.phone,
            },
          ],
          emailAddress: payload.contact_details.email,
          address: {
            lines: [payload.contact_details.address],
            postalCode: payload.contact_details.zipcode,
            cityName: payload.contact_details.city,
            countryCode: payload.contact_details.country_code,
          },
        },
      ],
    },
  };

  payload.traveler_details.forEach((traveler) => {
    flight_order_body.data.travelers.push({
      id: traveler.id,
      dateOfBirth: traveler.birth,
      name: {
        firstName: traveler.firstname,
        middleName: traveler.middlename,
        lastName: traveler.lastname,
        secondLastName: traveler.second_lastname,
      },
      gender: traveler.gender,

      /* TODO --> capire SE e QUANDO servono
      contacts: [
        {
          phones: [
            {
              deviceType: 'MOBILE',
              countryCallingCode: payload.contact_details.country_calling_code,
              number: payload.contact_details.phone                  
            }
          ],
          emailAddress: payload.contact_details.email,
        }
      ],
      */

      contact: {
        purpose: 'STANDARD',
        language: 'IT',
        emailAddress: traveler.email,
        phones: [
          {
            deviceType: 'MOBILE',
            countryCallingCode: payload.contact_details.country_calling_code,
            number: payload.contact_details.phone,
          },
        ],
      },

      /*documents: [
        {
          documentType: "PASSPORT",
          number: traveler.passport,
          expiryDate: traveler.passport_expiration_date,
          nationality: traveler.country_code,
          issuanceCountry: traveler.country_code, // TODO -> approfondire se può essere forzato con la residenza come ora
          holder: true, // TODO -> approfondire se può restare true
        }
      ]*/
    });
  });

  return flight_order_body;
}

export function apply_markup_to_number(flight_offer, n) {
  let mu_percent = get_markup(flight_offer).mu_percent,
    mu_price = round(sum(n, multiply(n, mu_percent)), decimals).toString();

  return mu_price;
}

export function get_markup(flight_offer) {
  let mu = JSON.parse(atob(Vue.prototype.$config.mu));
  let source = flight_offer.source || 'DEFAULT';
  let markups = mu[source] ? mu[source] : mu['DEFAULT'];
  const price = flight_offer.price;
  let d = {
    markup_base: number(price.total), // prezzo da considerare per la forbice mu
    grandTotal: number(price.grandTotal || price.total),
    grand_total_mu: 0,
    grand_total_with_mu: 0,
    source: source,
    source_markup: 0,
    all_markup: number(mu.ADD.p),
    mu_percent: number(mu.ADD.p),
    mu_amount: 0,
    mu_price: 0,
    mu_price_medium_per_pax: 0,
    mu_per_pax: 0,
    traveler_pricing: [],
    mu_per_pax_obj: [],
    additional_services_total: 0,
    additional_services_mu_amount: 0,
    additional_services_with_mu: 0,
    tot_fees: 0,
  };

  markups.forEach((m) => {
    if (largerEq(d.markup_base, number(m.min))) {
      d.source_markup = number(m.p);
    } else {
      return;
    }
  });

  if (Vue.prototype.$config.forced_mu) {
    // caso CONFIRM -> markup forzato da confirm()
    d.mu_percent = round(number(JSON.parse(atob(Vue.prototype.$config.forced_mu))), 3);
  } else {
    d.mu_percent = round(sum(d.source_markup, d.all_markup), 3);
  }

  // per gestire il markup dei bagagli nell'estrattoconto
  // occore considerare il total e mu dei servizi aggiunti
  // e sottrarli dal conteggio "tasse" (nostro mu per pax)
  // in quando una volta aggiunti sono considerati nel grandTotal ma hanno voci a parte
  if (flight_offer.price.additionalServices) {
    d.additional_services_total = flight_offer.price.additionalServices.reduce((carry, item) => {
      if (item.amount) carry = sum(carry, number(item.amount));
      return carry;
    }, d.additional_services_total);
  }

  if (d.additional_services_total)
    d.additional_services_mu_amount = round(
      multiply(d.additional_services_total, d.mu_percent),
      decimals
    );

  d.additional_services_with_mu = d.additional_services_total + d.additional_services_mu_amount;

  d.grand_total_mu = round(multiply(d.grandTotal, d.mu_percent), decimals);
  d.grand_total_with_mu = round(sum(d.grandTotal, d.grand_total_mu), decimals);
  d.mu_amount = round(d.grand_total_mu, decimals).toString();
  d.mu_price = round(d.grand_total_with_mu, decimals).toString();
  d.mu_price_medium_per_pax = d.mu_price;
  d.mu_per_pax = subtract(d.mu_amount, d.additional_services_mu_amount);

  if (flight_offer.travelerPricings) {
    // dividere per tutti i PAX senza distinzione di età https://trello.com/c/kbFpu0zB/1883-prezzo-per-pax
    // let travelers_excluded_infants = flight_offer.travelerPricings.filter( function(t) { return t.travelerType !== 'HELD_INFANT' }).length
    let travelers_excluded_infants = flight_offer.travelerPricings.length;

    d.mu_price_medium_per_pax = round(
      divide(d.grand_total_with_mu, travelers_excluded_infants),
      decimals
    ).toString();
    flight_offer.travelerPricings.forEach((traveler) => {
      let new_traveler = deep_clone(traveler);
      new_traveler.price.base = traveler.price.base
        ? round(
            sum(number(traveler.price.base), multiply(number(traveler.price.base), d.mu_percent)),
            decimals
          ).toString()
        : 0;
      new_traveler.price.total = round(
        sum(number(traveler.price.total), multiply(number(traveler.price.total), d.mu_percent)),
        decimals
      ).toString();
      if (traveler.price.taxes) {
        traveler.price.taxes.forEach((tax, t) => {
          new_traveler.price.taxes[t].amount = round(
            sum(number(tax.amount), multiply(number(tax.amount), d.mu_percent)),
            decimals
          ).toString();
        });
      }
      d.traveler_pricing.push(new_traveler);
    });

    //mu_per_pax = divide(mu_amount, flight_offer.travelerPricings.length)
    d.mu_per_pax = divide(
      subtract(d.mu_amount, d.additional_services_mu_amount),
      flight_offer.travelerPricings.length
    );
  }

  if (flight_offer.price.fees) {
    d.tot_fees = flight_offer.price.fees.reduce((carry, item) => {
      if (item.amount) carry = sum(carry, number(item.amount));
      return carry;
    }, d.tot_fees);
  }

  if (d.tot_fees > 0) {
    d.mu_per_pax = sum(d.mu_per_pax, divide(d.tot_fees, flight_offer.travelerPricings.length));
  }

  d.mu_per_pax = round(d.mu_per_pax, decimals);

  /*
  creo un oggetto con markup specifico per pax
  li sommo e sottraggo la differenza all'ultimo
  per risolvere problemi di arrotondamenti
   */
  for (let i = 0; i < flight_offer.travelerPricings.length; i++) {
    d.mu_per_pax_obj[i] = d.mu_per_pax;
  }

  try {
    d.mu_per_pax_obj[flight_offer.travelerPricings.length - 1] = round(
      sum(
        d.mu_per_pax_obj[flight_offer.travelerPricings.length - 1],
        subtract(
          subtract(sum(d.mu_amount, d.tot_fees), d.additional_services_mu_amount), // le fees sono considerate nel grandTotal ma non nel tot per pax, vanno considerate come nostre "tasse e spese amministrative" per far tornare i totali nella sidebar resume
          multiply(d.mu_per_pax, flight_offer.travelerPricings.length)
        )
      ),
      decimals
    );
  } catch (e) {
    window.console.log(e); // TODO
  }

  let resp = {
    mu_price: d.mu_price,
    mu_amount: d.mu_amount,
    mu_percent: d.mu_percent,
    mu_per_pax: d.mu_per_pax,
    mu_price_medium_per_pax: d.mu_price_medium_per_pax,
    mu_per_pax_obj: d.mu_per_pax_obj,
    traveler_pricing: d.traveler_pricing,
    additional_services_total: d.additional_services_total,
    additional_services_mu_amount: d.additional_services_mu_amount,
    additional_services_with_mu: d.additional_services_with_mu,
    //flight_offer: flight_offer,
  };
  /*
  if(process.env.VUE_APP_MODE == 'development'){
    window.console.table(d)
    window.console.table(resp)
  }*/

  return resp;
}

export function add_flexible_bag_to_flight_offer(flight_offer, flexible_bags) {
  // Deep clone to ensure a new reference is committed to Vuex,
  // so Vue 2's reactivity reliably detects the change.
  const updated_offer = deep_clone(flight_offer);
  let has_flexible_selection = false;
  updated_offer.travelerPricings.forEach((traveler) => {
    traveler.fareDetailsBySegment.forEach((fare_detail_by_segment) => {
      if (traveler.travelerType === 'HELD_INFANT' || traveler.travelerType === 'SEATED_INFANT')
        return;
      fare_detail_by_segment.additionalServices = {};
    });
  });
  Object.keys(flexible_bags).forEach((t) => {
    let traveler = flexible_bags[t];
    Object.keys(traveler).forEach((i) => {
      let itinerary = traveler[i];
      Object.keys(itinerary).forEach((b) => {
        if (!itinerary[b].added) return;
        let flexible_bag = itinerary[b].bag;
        has_flexible_selection = true;
        let traveler_priced = updated_offer.travelerPricings[t];
        traveler_priced.fareDetailsBySegment.forEach((s) => {
          if (!flexible_bag.segmentIds.includes(s.segmentId)) return;
          if (
            traveler_priced.travelerType === 'HELD_INFANT' ||
            traveler_priced.travelerType === 'SEATED_INFANT'
          )
            return;
          if (
            updated_offer.itineraries[i].segments.filter((segment) => {
              return segment.id == s.segmentId;
            }).length > 0
          )
            s.additionalServices.chargeableCheckedBags = flexible_bag;
        });
      });
    });
  });
  delete updated_offer.price.additionalServices;
  Store.commit('liveflight/has_flexible_bags_last_selection', has_flexible_selection);
  return updated_offer;
}

/*
export function add_bag_to_flight_offer(flight_offer, bag, grand_total) {
  flight_offer.price.grandTotal = grand_total
  let new_grand_total = number(flight_offer.price.grandTotal),
      bag_amount = number(bag.price.amount)

  // se bookableByItinerary == true => moltiplico il costo del bagaglio per il numero di itinerari
  if( bag.bookableByItinerary ) {
    bag_amount = multiply( bag_amount, flight_offer.itineraries.length )
  }

  flight_offer.travelerPricings.forEach((traveler,t) => {

    if(traveler.travelerType === 'HELD_INFANT' || traveler.travelerType === 'SEATED_INFANT' || traveler.travelerType === 'CHILD') return

    new_grand_total = round( sum( new_grand_total, bag_amount ), decimals ).toString()

    traveler.fareDetailsBySegment.forEach((detail,d) => {
      (detail)
      if( flight_offer.travelerPricings[t].fareDetailsBySegment[d].additionalServices == undefined ) flight_offer.travelerPricings[t].fareDetailsBySegment[d].additionalServices = {}
      flight_offer.travelerPricings[t].fareDetailsBySegment[d].additionalServices.chargeableCheckedBags = bag
    })
  })

  flight_offer.price.grandTotal = new_grand_total

  return flight_offer
}

export function remove_bag_from_flight_offer(flight_offer, grand_total) {
  flight_offer.price.grandTotal = grand_total
  flight_offer.travelerPricings.forEach((traveler,t) => {
    traveler.fareDetailsBySegment.forEach((detail,d) => {
      (detail)
      if(flight_offer.travelerPricings[t].fareDetailsBySegment[d].additionalServices) delete flight_offer.travelerPricings[t].fareDetailsBySegment[d].additionalServices.chargeableCheckedBags
    })
  })
  return flight_offer
}
*/

/**
 * Add to a specified flight offer the seats selected by the user.
 * @param {FlightOfferObj} flight_offer The initial flight offer
 * @param {{[segment: string]: {[passenger: string]: Seat}}} seats The seat disposition for each segment and each passenger
 * @returns A copy of the original flight_offer with the seat selection information
 */
export function addSelectedSeatsToFlightOffer(flight_offer, seats) {
  let additionalPrice = 0;
  const updatedFlightOffer = {
    ...flight_offer,
    travelerPricings: flight_offer.travelerPricings.map((pricing) => ({
      ...pricing,
      fareDetailsBySegment: pricing.fareDetailsBySegment.map((fareDetails) => {
        const { segmentId } = fareDetails;
        const seatsPerSegment = seats[segmentId];

        if (!seatsPerSegment) {
          return fareDetails;
        }

        const { travelerId } = pricing;
        const seat = seatsPerSegment[travelerId];

        if (!seat) {
          const removedSeat = { ...fareDetails };
          if (removedSeat.additionalServices) {
            delete removedSeat.additionalServices.chargeableSeatNumber;
            if (Object.keys(removedSeat.additionalServices).length === 0) {
              delete removedSeat.additionalServices;
            }
          }
          return removedSeat;
        }

        additionalPrice += getSeatPrice(seat, travelerId, false);

        return {
          ...fareDetails,
          additionalServices: {
            ...(fareDetails.additionalServices || {}),
            chargeableSeatNumber: seat.number,
          },
        };
      }),
    })),
  };
  const price = flight_offer.price;
  const grandTotal =
    Math.ceil((Number(price.grandTotal || price.total) + additionalPrice) * 100) / 100;

  return {
    ...updatedFlightOffer,
    price: {
      ...price,
      grandTotal,
    },
  };
}

export const getSegmentSelectedSeatsFromFlightOffer = (
  flight_offer,
  segmentId,
  segmentSeats = []
) => {
  if (!flight_offer || !flight_offer.travelerPricings) {
    return {};
  }

  const seatMap = segmentSeats.reduce(
    (map, seat) => ({
      ...map,
      [seat.number]: seat,
    }),
    {}
  );

  const selections = {};
  const travelerPricings = flight_offer.travelerPricings;

  travelerPricings.forEach((pricing) => {
    const fareDetailsBySegment = pricing.fareDetailsBySegment || [];
    const { travelerId } = pricing;
    fareDetailsBySegment
      .filter((fareDetails) => fareDetails.segmentId === segmentId)
      .forEach((fareDetails) => {
        const additionalServices = fareDetails.additionalServices || {};
        const chargeableSeatNumber = additionalServices.chargeableSeatNumber;
        if (chargeableSeatNumber) {
          if (segmentSeats.length > 0) {
            const seat = seatMap[chargeableSeatNumber];
            if (seat && isSeatAvailableForPassenger(travelerId, seat)) {
              selections[travelerId] = seat;
            }
          } else {
            selections[travelerId] = chargeableSeatNumber;
          }
        }
      });
  });

  return selections;
};

/**
 * Assign an adult passenger to the associatedAdultId of a infant.
 * @param {FlightOfferObj} flight_offer The initial flight offer
 * @param {{[infantId: string]: string}} heldPassengers a map linking infantId to associatedAdultId
 * @returns A copy of the original flight_offer with the updated infant
 */
export function set_infant_associated_adult_id(flight_offer, heldPassengers) {
  const updatedFlightOffer = {
    ...flight_offer,
    travelerPricings: flight_offer.travelerPricings.map((pricing) => {
      const associatedAdultId = heldPassengers[pricing.travelerId];
      if (!associatedAdultId) {
        return pricing;
      }
      if (pricing.travelerType !== 'HELD_INFANT') {
        window.console.log('Trying to assign associatedAdultId for a non infant passenger');
        return pricing;
      }
      return {
        ...pricing,
        associatedAdultId,
      };
    }),
  };
  return updatedFlightOffer;
}

export function has_chargeable_bags(flight_offer, traveler_id) {
  let has_bags = false;
  flight_offer.travelerPricings
    .filter((t) => {
      return t.travelerId == traveler_id;
    })[0]
    .fareDetailsBySegment.forEach((fdbs) => {
      if (fdbs.additionalServices !== undefined) {
        if (fdbs.additionalServices.chargeableCheckedBags !== undefined) {
          if (fdbs.additionalServices.chargeableCheckedBags.quantity > 0) has_bags = true;
        }
      }
    });
  return has_bags;
}

export const travelerHasSelectedSeats = (flightOffer, travelerId) => {
  try {
    const pricings = flightOffer.travelerPricings.filter((t) => t.travelerId === travelerId);
    for (let i = 0; i < pricings.length; i += 1) {
      const fareDetailsBySegment = pricings[i].fareDetailsBySegment;
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            return true;
          }
        }
      }
    }
  } catch (e) {
    log('travelerHasSelectedSeats error');
    log(e);
    return false;
  }
  return false;
};

export const segmentHasSelectedSeats = (flightOffer, segmentId) => {
  try {
    const pricings = flightOffer.travelerPricings;
    for (let i = 0; i < pricings.length; i += 1) {
      const fareDetailsBySegment = (pricings[i].fareDetailsBySegment || []).filter(
        (fare) => fare.segmentId === segmentId
      );
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            return true;
          }
        }
      }
    }
  } catch (e) {
    log('travelerHasSelectedSeats error');
    log(e);
    return false;
  }
  return false;
};

export function has_included_bags(flight_offer, traveler_id) {
  let has_bags = false;
  flight_offer.travelerPricings
    .filter((t) => {
      return t.travelerId == traveler_id;
    })[0]
    .fareDetailsBySegment.forEach((fdbs) => {
      if (fdbs.includedCheckedBags !== undefined) {
        if (fdbs.includedCheckedBags.quantity && fdbs.includedCheckedBags.quantity > 0)
          has_bags = true;
        if (fdbs.includedCheckedBags.weight && fdbs.includedCheckedBags.weight > 0) has_bags = true;
      }
    });
  return has_bags;
}

const bag_quantity = (bag) => {
  if (bag.quantity) {
    return bag.quantity;
  } else if (bag.weight) {
    return 1;
  }
  return 0;
};

export function parse_flight_offer_bags(flight_offer, itinerary_index = 0) {
  const parsed = {
    extra: false,
    travelers: [],
    cabin: 0,
    included: 0,
    chargeable: 0,
    total: 0,
  };

  const { travelerPricings = [], itineraries = [] } = flight_offer || {};
  // Sanity check
  if (itinerary_index < 0 || itinerary_index >= itineraries.length) {
    return parsed;
  }

  const { segments: itinerary_segments = [] } = itineraries[itinerary_index];
  // Sanity check
  if (itinerary_segments.length === 0) {
    return parsed;
  }
  const first_segment_of_current_itinerary = itinerary_segments[0].id;

  travelerPricings.forEach((traveler_pricing) => {
    const fare_details_by_segment = (traveler_pricing.fareDetailsBySegment || []).filter(
      (f) => f.segmentId === first_segment_of_current_itinerary
    )[0];
    const traveler = {
      traveler_id: traveler_pricing.travelerId,
      traveler_type: traveler_pricing.traveler_type || traveler_pricing.travelerType,
      cabin: 0,
      included: 0,
      chargeable: 0,
    };

    if (fare_details_by_segment) {
      // gestione includedCheckedBags
      if (fare_details_by_segment.includedCheckedBags) {
        traveler.included = bag_quantity(fare_details_by_segment.includedCheckedBags);
        parsed.included = sum(parsed.included, traveler.included);
      }

      // gestione includedCabinBags
      if (fare_details_by_segment.includedCabinBags) {
        traveler.cabin = bag_quantity(fare_details_by_segment.includedCabinBags);
        parsed.cabin = sum(parsed.cabin, traveler.cabin);
      }

      // gestione chargeableCheckedBags
      if (
        fare_details_by_segment.additionalServices &&
        fare_details_by_segment.additionalServices.chargeableCheckedBags
      ) {
        traveler.chargeable = bag_quantity(
          fare_details_by_segment.additionalServices.chargeableCheckedBags
        );
        parsed.chargeable = sum(parsed.chargeable, traveler.chargeable);
      }
    }

    // gestione extra disponibili
    if (flight_offer.price) {
      // verifica che non siano già stati aggiunti bagagli, nel qual caso NON mostrare gli extra
      if (fare_details_by_segment && fare_details_by_segment.additionalServices) {
        if (fare_details_by_segment.additionalServices.chargeableCheckedBags) {
          parsed.extra = false; // ridondante, per sicurezza
        }
      } else if (flight_offer.price.additionalServices) {
        const additional_services = flight_offer.price.additionalServices.filter(
          (s) => s.type === 'CHECKED_BAGS'
        );
        if (additional_services.length) {
          parsed.extra = true;
        }
      }
    }

    parsed.travelers.push(traveler);
  });

  return {
    ...parsed,
    total: sum(parsed.included, parsed.chargeable),
  };
}

export function get_taxes_amount(flight_offer) {
  let amount = 0;

  flight_offer.travelerPricings.forEach((traveler) => {
    if (traveler.price.taxes) {
      traveler.price.taxes.forEach((tax) => {
        amount = sum(amount, parseFloat(tax.amount));
      });
    }
  });

  amount = round(amount, decimals);

  return {
    amount: amount,
    amount_with_markup: apply_markup_to_number(flight_offer, amount),
  };
}

export function transcode_additional_services(service_type, service_quantity = 1) {
  let label = '';
  switch (service_type) {
    case 'CHECKED_BAGS':
      label = service_quantity > 1 ? 'bagagli extra in stiva' : 'bagaglio extra in stiva';
      break;

    case 'MEALS':
      label = service_quantity > 1 ? 'pasti' : 'pasto';
      break;

    case 'SEATS':
      label = service_quantity > 1 ? 'posti a sedere' : 'posto a sedere';
      break;

    default: //OTHER_SERVICES
      label = 'altri servizi';
      break;
  }
  return label;
}

export function get_additional_service_tooltip(service_type) {
  switch (service_type) {
    case 'CHECKED_BAGS':
      return 'Sono i bagagli aggiunti e non inclusi nella tariffa iniziale.';
  }
  return '';
}

export function transcode_traveler_type(traveler_type, traveler_quantity = 1) {
  let label = '';
  switch (traveler_type) {
    case 'ADULT':
      label = traveler_quantity > 1 ? 'adulti' : 'adulto';
      break;

    case 'CHILD':
      label = traveler_quantity > 1 ? 'bambini' : 'bambino';
      break;

    case 'HELD_INFANT':
      label = traveler_quantity > 1 ? 'neonati' : 'neonato';
      break;

    case 'SEATED_INFANT':
      label = traveler_quantity > 1 ? 'neonati' : 'neonato';
      break;

    default:
      break;
  }
  return label;
}

export function get_flight_offer_dates(flight_offer) {
  let first_itinerary = flight_offer.itineraries[0],
    first_segment = first_itinerary.segments[0],
    last_itinerary = flight_offer.itineraries[flight_offer.itineraries.length - 1],
    last_segment = last_itinerary.segments[last_itinerary.segments.length - 1];

  return {
    departure: first_segment.departure,
    return: flight_offer.itineraries.length > 1 ? last_segment.departure : '',
  };
}

export function select_flight_offer(flight_offer, path = '') {
  Store.commit('liveflight/select_flight_offer', flight_offer);
  Store.commit('booking_data', {});
  if (path === '') return;
  if (Router.history.current.path === path) return;
  Router.push({ path: path });
}

export function get_traveler_birth_range_dates(flight_offer, traveler_id) {
  // formato di ritorno richiesto dal picker: 2024-03-20

  let departure = flight_offer.itineraries[0].segments[0].departure.at,
    traveler = flight_offer.travelerPricings.filter(function (t) {
      return t.travelerId == traveler_id;
    })[0],
    min = new Date(departure),
    max = new Date(departure);

  switch (traveler.travelerType) {
    // ADULTS >= 12y
    case 'ADULT':
      // min: imposto l'anno a 1900 (non posso lasciarlo vuoto o undefined)
      min = new Date(min.setFullYear(1900));

      if (traveler_id == 1) {
        // max: il primo pax deve necessariamente essere maggiorenne
        max = new Date(max.setFullYear(max.getFullYear() - 18));
      } else {
        // max: tolgo 12 anni esatti (può partire nel giorno in cui compie 12 anni)
        max = new Date(max.setFullYear(max.getFullYear() - 12));
      }

      break;

    // CHILD < 12y
    case 'CHILD':
      // min: tolgo 12 anni e sommo un giorno (altrimenti può partire nel giorno in cui compie 12 anni)
      min = new Date(min.setFullYear(min.getFullYear() - 12));
      min = new Date(min.setDate(min.getDate() + 1));
      // max: tolgo 2 anni esatti (può partire nel giorno in cui compie 2 anni)
      max = new Date(max.setFullYear(max.getFullYear() - 2));
      break;

    // INFANT (HELD/SEATED) < 2Y
    case 'HELD_INFANT':
    case 'SEATED_INFANT':
      // min: tolgo 12 anni e sommo un giorno (altrimenti può partire nel giorno in cui compie 2 anni)
      min = new Date(min.setFullYear(min.getFullYear() - 2));
      min = new Date(min.setDate(min.getDate() + 1));
      // max: metto la giornata di ieri altrimenti il server mi risponderà 400 al checkout con NOT BORN YET se lascio la departure
      max = new Date();
      max = new Date(max.setDate(max.getDate() - 1));
      break;

    default:
      break;
  }

  min = format_date_YYYY_MM_DD(min, '-');
  max = format_date_YYYY_MM_DD(max, '-');

  return {
    min: min,
    max: max,
  };
}

export const get_paid_additional_services = (flight_offer) => {
  try {
    if (!flight_offer || !flight_offer.price) {
      return [];
    }
    const additionalServices = flight_offer.price.additionalServices || [];
    return additionalServices.filter((addition) => Number(addition.amount || '-1') >= 0);
  } catch (e) {
    return [];
  }
};

export const get_flight_offer_rybbit_data = (flight_offer) => {
  if (!flight_offer) return {};

  const itineraries = (flight_offer.itineraries || []).map((itinerary) => {
    const segments = itinerary.segments || [];
    const first_segment = segments[0];
    const last_segment = segments[segments.length - 1];

    return {
      departure_airport: first_segment && first_segment.departure && first_segment.departure.iataCode,
      arrival_airport: last_segment && last_segment.arrival && last_segment.arrival.iataCode,
      departure_date: first_segment && first_segment.departure && first_segment.departure.at,
      arrival_date: last_segment && last_segment.arrival && last_segment.arrival.at,
      duration: itinerary.duration,
      stops: segments.length - 1,
      carrier: first_segment && first_segment.carrierCode,
    };
  });

  const traveler_pricings = flight_offer.travelerPricings || [];
  const first_fare = traveler_pricings[0] && traveler_pricings[0].fareDetailsBySegment && traveler_pricings[0].fareDetailsBySegment[0];

  return {
    source: flight_offer.source || '',
    branded_fare: (first_fare && first_fare.brandedFare) || '',
    itineraries,
  };
};

export const seats_per_segment = (flight_offer, traveler_details) => {
  try {
    const segments = {};
    const travelerPricings = flight_offer.travelerPricings || [];
    for (let index = 0; index < travelerPricings.length; index += 1) {
      const { travelerId, fareDetailsBySegment = [] } = travelerPricings[index];
      for (let fare_index = 0; fare_index < fareDetailsBySegment.length; fare_index += 1) {
        const { segmentId, additionalServices = {} } = fareDetailsBySegment[fare_index];
        if (additionalServices.chargeableSeatNumber) {
          const { firstname, lastname } = traveler_details.find((t) => t.id === travelerId) || {};

          const traveler_name =
            firstname && lastname ? `${firstname} ${lastname}` : `P${travelerId}`;
          segments[segmentId] = [
            ...(segments[segmentId] || []),
            {
              chargeableSeatNumber: additionalServices.chargeableSeatNumber,
              traveler_name,
              travelerId,
            },
          ];
        }
      }
    }
    return segments;
  } catch (e) {
    return {};
  }
};
